import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Stock audit (Reports → Stock audit). Phase 0 of docs/STOCK-SPEC.md.
//
// The point of this page is one number: how much of the tracked catalog is
// marked unavailable, and how much of that is wrong. On 2026-09-06 it was 104
// of 143 sold out with 46 provably phantom; five days later Diet Coke at
// Bellevue had drifted from -60 to -81. The data degrades on its own, so the
// number needs somewhere to be looked at rather than a script someone
// remembers to run.
//
// Read-only for now. The clearing action is Docket #255, still open.
export function StockAuditView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollKey, setPollKey] = useState(0);

  // The scan takes minutes, so the server never blocks on it: a request returns
  // the last computed result and rebuilds in the background. While it is
  // rebuilding we poll, showing the previous numbers with a "scanning" note
  // rather than an empty page or a request that dies at the gateway.
  async function load(refresh) {
    setLoading(true);
    setError(null);
    try {
      const r = await api('GET', `/api/stock/audit${refresh ? '?refresh=1' : ''}`, null, headers);
      if (r && r.available === false) setError(r.reason || 'Square is unreachable');
      else {
        if (r.computing && !r.summary) setData(prev => prev || r);
        else setData(r);
        return !!r.computing;
      }
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
    return false;
  }

  useEffect(() => {
    let stop = false;
    let timer = null;
    (async function tick(refresh) {
      const computing = await load(refresh);
      if (computing && !stop) timer = setTimeout(() => tick(false), 5000);
    })(false);
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [pollKey]);

  const s = data?.summary;
  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

  const card = { border: '1px solid #e6e9ec', borderRadius: 10, padding: '12px 14px', background: '#fff' };
  const th = { padding: '6px 8px', textAlign: 'left', color: '#666', fontSize: 11, letterSpacing: '0.04em' };
  const td = { padding: '7px 8px', borderTop: '1px solid #eceff1' };
  const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  function Tile({ label, value, sub, tone }) {
    const tones = {
      bad: { fg: '#a13030', bg: '#fef2f2', bd: '#fecaca' },
      warn: { fg: '#8e6c33', bg: '#fef9ee', bd: '#f5e6c8' },
      plain: { fg: '#333', bg: '#fafafa', bd: '#e6e9ec' },
    };
    const t = tones[tone] || tones.plain;
    return (
      <div style={{ ...card, background: t.bg, borderColor: t.bd, flex: '1 1 150px', minWidth: 150 }}>
        <div style={{ fontSize: 11, color: '#666', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700, color: t.fg, lineHeight: 1.2 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>{sub}</div>}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <div className="text-base font-semibold">Stock audit</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {data?.computing && data?.summary && (
            <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99, fontWeight: 600,
                            background: '#e8f5fe', color: '#1565c0', border: '1px solid #b3d6f5' }}>
              rescanning — numbers below are the last completed scan
            </span>
          )}
          {data?.stale && !data?.computing && (
            <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99, fontWeight: 600,
                            background: '#fef9ee', color: '#8e6c33', border: '1px solid #f5e6c8' }}>
              stale — Square unreachable, showing the last good scan
            </span>
          )}
          {data?.generated_at && (
            <span className="text-sm text-gray-500">
              {new Date(data.generated_at).toLocaleString()}
            </span>
          )}
          <button onClick={() => setPollKey(k => k + 1)} disabled={loading || data?.computing}
            style={{ fontSize: 12, padding: '5px 12px', border: '1px solid #d0d4d9', borderRadius: 6,
                      background: '#fff', cursor: loading ? 'default' : 'pointer' }}>
            {loading || data?.computing ? 'Scanning…' : 'Rescan'}
          </button>
        </div>
      </div>
      <div className="text-sm text-gray-500 mb-4">
        What Square believes is on the shelf at the four cafés, and where that has
        stopped being true. Counting fixes it; this page is how you see whether it worked.
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#a13030',
                      borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {(loading || data?.computing) && !data?.summary && (
        <div style={{ ...card, color: '#666', fontSize: 13 }}>
          Scanning the full Square catalog, current counts, and 30 days of inventory
          changes across four cafés. The first load takes up to a minute.
        </div>
      )}

      {s && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <Tile label="TRACKED" value={s.tracked_pairs}
              sub={`${s.distinct_items} items across 4 cafés`} />
            <Tile label="MARKED SOLD OUT" value={s.sold_out}
              sub={`${pct(s.sold_out, s.tracked_pairs)}% of the tracked catalog`}
              tone={s.sold_out > s.tracked_pairs / 2 ? 'bad' : 'warn'} />
            <Tile label="PHANTOM" value={s.phantom}
              sub="sold out, yet sold this week" tone={s.phantom ? 'bad' : 'plain'} />
            <Tile label="NEGATIVE COUNTS" value={s.negative}
              sub="restocks never recorded" tone={s.negative ? 'warn' : 'plain'} />
            <Tile label="NO LOW-STOCK ALERT" value={`${s.alerts_off}/${s.tracked_pairs}`}
              sub="no early warning anywhere" tone={s.alerts_off > 0 ? 'warn' : 'plain'} />
          </div>

          <div style={{ ...card, marginBottom: 14 }}>
            {/* Capped rather than full-bleed: five short columns stretched across
                a wide monitor puts the number a foot from its label. */}
            <table style={{ width: '100%', maxWidth: 620, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>CAFÉ</th>
                  <th style={{ ...th, textAlign: 'right' }}>TRACKED</th>
                  <th style={{ ...th, textAlign: 'right' }}>SOLD OUT</th>
                  <th style={{ ...th, textAlign: 'right' }}>NEGATIVE</th>
                  <th style={{ ...th, textAlign: 'right' }}>ALERTS OFF</th>
                </tr>
              </thead>
              <tbody>
                {data.by_location.map(l => (
                  <tr key={l.location}>
                    <td style={td}><strong>{l.location}</strong></td>
                    <td style={num}>{l.tracked}</td>
                    <td style={{ ...num, color: l.soldOut > l.tracked / 2 ? '#a13030' : '#333',
                                  fontWeight: l.soldOut > l.tracked / 2 ? 700 : 400 }}>
                      {l.soldOut}
                    </td>
                    <td style={{ ...num, color: l.negative ? '#a13030' : '#333' }}>{l.negative}</td>
                    <td style={num}>{l.alertsOff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ ...card, marginBottom: 14 }}>
            <div className="text-base font-semibold mb-1">
              Phantom stockouts <span style={{ color: '#a13030' }}>({data.phantoms.length})</span>
            </div>
            <div className="text-sm text-gray-500 mb-3">
              Marked unavailable, yet sold within the last 7 days — so the product is on
              the shelf and Square does not know it. Staff ring these at the counter
              regardless, which is why the counts run negative rather than resting at zero.
              The loss is on the order-ahead app and the online store, where nothing
              overrides the flag.
            </div>
            {data.phantoms.length === 0 ? (
              <div className="text-sm text-gray-500">None. Every sold-out item has actually stopped selling.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
                  <thead>
                    <tr>
                      <th style={th}>CAFÉ</th>
                      <th style={th}>ITEM</th>
                      <th style={{ ...th, textAlign: 'right' }}>ON HAND</th>
                      <th style={{ ...th, textAlign: 'right' }}>LAST SOLD</th>
                      <th style={{ ...th, textAlign: 'right' }}>LAST RESTOCK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.phantoms.map((p, i) => (
                      <tr key={i}>
                        <td style={td}>{p.location}</td>
                        <td style={td}>{p.item} <span style={{ color: '#888' }}>/ {p.variation}</span></td>
                        <td style={{ ...num, color: p.qty < 0 ? '#a13030' : '#333' }}>{p.qty}</td>
                        <td style={num}>{p.lastSaleDays === 0 ? 'today' : `${p.lastSaleDays}d ago`}</td>
                        <td style={{ ...num, color: p.lastRestockDays == null ? '#a13030' : '#333' }}>
                          {p.lastRestockDays == null ? `never in ${data.change_window_days}d` : `${p.lastRestockDays}d ago`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={card}>
            <div className="text-base font-semibold mb-1">
              Negative counts <span style={{ color: '#a13030' }}>({data.negatives.length})</span>
            </div>
            <div className="text-sm text-gray-500 mb-3">
              Square thinks fewer than zero are on hand, which only happens when an item
              keeps selling after a restock nobody entered. The size of the number is how
              long it has been going on.
            </div>
            {data.negatives.length === 0 ? (
              <div className="text-sm text-gray-500">None.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 440 }}>
                  <thead>
                    <tr>
                      <th style={th}>CAFÉ</th>
                      <th style={th}>ITEM</th>
                      <th style={{ ...th, textAlign: 'right' }}>ON HAND</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.negatives.map((n, i) => (
                      <tr key={i}>
                        <td style={td}>{n.location}</td>
                        <td style={td}>{n.item} <span style={{ color: '#888' }}>/ {n.variation}</span></td>
                        <td style={{ ...num, color: '#a13030', fontWeight: 600 }}>{n.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
